/**
 * Where ClassCalc's rules come from, and how old they are.
 *
 * Every answer this tool gives is a reading of somebody else's rulebook, so the
 * page has to say whose and from when. That is not politeness — a racer who
 * takes a checklist from here into a tech line and finds it out of date needs to
 * know, at a glance, which document to go and read instead.
 *
 * The links were checked live and point at pages that are maintained rather than
 * at year-stamped PDFs, which rot the moment a season turns over:
 *   - nhraracer.com/rules is where the current rulebook and its amendments are
 *     published, and nhraracer.com/license-information carries every licence
 *     application and the licence regulations printed on the back of them.
 *     (The old `tech.asp` addresses this file shipped with are gone: the site
 *     was rebuilt off ASP and every one of them now answers 400. Rot in the
 *     other direction from a stale PDF, and the reason the checked stamp below
 *     is a date rather than a promise.)
 *   - chassisinspector.nhradata.com is the working inspector locator.
 *     (nhra.com/chassis-inspector-locator renders empty, and the old
 *     cms.nhra.com host now serves an expired certificate — see safety-kit.ts,
 *     which pointed at it.)
 *   - ihra.com/racing/rules is IHRA's rules hub.
 *   - x275rules.com, which this file pointed at for the two radial classes with
 *     published rule sets, no longer resolves at all. The Radial Outlaws rules
 *     page has taken its place, which is the better address anyway: it is where
 *     they race.
 *
 * On versioning: the build plan's data-source note asks for rule data the
 * maintainer can update without a full redeploy. That is deliberately not what
 * happens here. Fetching the ladder at runtime would break §5's offline
 * requirement, and the deploy syncs S3 with `--delete`, so a hand-uploaded JSON
 * would survive exactly until the next push. The data is compiled in and stamped
 * instead: editing `safety.ts` or `classes.ts` and pushing is the update path,
 * and `RULES_SEASON` is what tells a reader whether that has been done.
 */

/**
 * The season these rules were transcribed from. Bump it when they are revised.
 *
 * The radial classes are the exception this stamp cannot cover, and the entries
 * say so themselves: X275 and Ultra Street publish their own rule sets on their
 * own schedule, Limited Drag Radial and Radial vs the World are run to whatever
 * the promoter posts for that weekend, and all of it is revised mid-season for
 * parity. A single season number over that scene would be a promise nobody can
 * keep — so those classes carry "check the current rules for the race you are
 * entering" in their notes rather than leaning on this.
 */
export const RULES_SEASON = '2026';

/** When the transcription was last checked against the published rulebooks. */
export const RULES_CHECKED = 'August 2026';

/**
 * The four documents `credentials.ts` sends people to, by name.
 *
 * They live here rather than beside the prose that links to them because this
 * is the file that gets checked when something rots, and a URL buried in a
 * paragraph of reassurance is a URL nobody re-checks. Every one of these was
 * live as of `RULES_CHECKED`.
 */
export const LICENCE_INFORMATION = 'https://www.nhraracer.com/license-information';
export const LICENCE_APPLICATION_67 = 'https://www.nhra.net/nhraracer/files/Level6and7Sportsman.pdf';
export const CHASSIS_INSPECTOR = 'https://chassisinspector.nhradata.com/';
export const SFI_CHASSIS = 'https://sfifoundation.com/drag-racing-chassis/';

export interface RuleSource {
  org: 'NHRA' | 'IHRA' | 'SFI' | 'Drag Week' | 'Sick Week' | 'Radial';
  label: string;
  url: string;
  /** What a racer would open it for. */
  note: string;
}

export const RULE_SOURCES: RuleSource[] = [
  {
    org: 'NHRA',
    label: 'NHRA rulebook & tech updates',
    url: 'https://www.nhraracer.com/rules',
    note: 'The current rulebook, plus the amendments issued during the season.',
  },
  {
    org: 'NHRA',
    label: 'NHRA licence applications & regulations',
    url: LICENCE_INFORMATION,
    note: 'Every level’s application form, and the licence regulations printed on the back of them — which levels cover which ET, and what the runs are.',
  },
  {
    org: 'NHRA',
    label: 'NHRA chassis inspector locator',
    url: CHASSIS_INSPECTOR,
    note: 'Certification is required at 9.99 and quicker; sportsman tags run three years.',
  },
  {
    org: 'IHRA',
    label: 'IHRA rules & rulebooks',
    url: 'https://ihra.com/racing/rules',
    note: 'General regulations and the class rules for the Summit SuperSeries.',
  },
  {
    org: 'Drag Week',
    label: 'HOT ROD Drag Week 2025–2027 rules',
    url: 'https://s3.amazonaws.com/uploads.webconnex.com/10977/comm_center/82qqxw2alj6/1737649807087-2025+-+2027+HRDW.pdf',
    note: 'The official rules and procedures, covering all three seasons. Every Drag Week class here comes from it.',
  },
  {
    org: 'Sick Week',
    label: 'Sick Week / Sick Summer 2026 rulebook',
    url: 'https://www.sickthemagazine.com/sick-week',
    note: 'Sick the Magazine’s event page, where the current rulebook revision is published.',
  },
  {
    org: 'Radial',
    label: 'X275 & Ultra Street class rules',
    url: 'https://radialoutlaws.com/pages/rules-and-procedures',
    note: 'The published rules for the two radial classes on this page that have them. Both are revised for parity during the season.',
  },
  {
    org: 'Radial',
    label: 'Radial Outlaws series',
    url: 'https://radialoutlaws.com/',
    note: 'Where the radial classes actually race, and whose per-event rules override anything summarised here.',
  },
  {
    org: 'SFI',
    label: 'SFI Foundation',
    url: 'https://sfifoundation.com/',
    note: 'What each SFI spec number actually covers, and which need recertifying.',
  },
  {
    org: 'SFI',
    label: 'SFI drag racing chassis specs',
    url: SFI_CHASSIS,
    note: 'The 25.x, 2.x and 10.x chassis specifications by name — the ET band, the weight ceiling and the construction each one covers.',
  },
];

export const DISCLAIMER =
  'This tool is a planning aid only. Always verify with the current official rulebook and your tech inspector.';
